import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useFeedback } from "../contexts/FeedbackContext";
import { useQuota } from "../contexts/QuotaContext";
import {
  Search,
  Disc,
  Link as LinkIcon,
  Music2,
  Loader2,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  Zap,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewSearch = () => {
  const navigate = useNavigate();
  const { triggerFeedbackCheck } = useFeedback();
  const { unlimited, remaining, used, limit: quotaLimit, canUse, fetchQuota } = useQuota();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("isrc");

  const quotaExhausted = !unlimited && !canUse("single_search");

  // Form states
  const [isrc, setIsrc] = useState("");
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");

  const handleSearch = async () => {
    let payload = {};

    switch (activeTab) {
      case "isrc":
        if (!isrc.trim()) {
          toast.error("Please enter an ISRC");
          return;
        }
        payload = { query_isrc: isrc.trim().toUpperCase() };
        break;
      case "spotify":
        if (!spotifyUrl.trim()) {
          toast.error("Please enter a Spotify URL or ID");
          return;
        }
        payload = { query_url: spotifyUrl.trim() };
        break;
      case "title":
        if (!title.trim()) {
          toast.error("Please enter a track title");
          return;
        }
        payload = {
          query_title: title.trim(),
          query_artist: artist.trim() || null,
        };
        break;
      default:
        return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/search`, payload);
      toast.success("Search started");
      
      // Trigger feedback check after successful search
      triggerFeedbackCheck("single_search");
      fetchQuota();
      
      navigate(`/search/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="new-search-page">
      {/* Quota Banner */}
      {!unlimited && (
        <div
          className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
            quotaExhausted
              ? "bg-red-500/10 border-red-500/30 text-red-300"
              : "bg-syncchain-accent/10 border-syncchain-accent/30 text-syncchain-text-secondary"
          }`}
          data-testid="search-quota-banner"
        >
          <div className="flex items-center gap-2 text-sm">
            {quotaExhausted ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <Search className="w-4 h-4" />}
            <span>
              {quotaExhausted
                ? "You've used all your single song searches."
                : `${used("single_search")} / ${quotaLimit("single_search")} single searches used`}
            </span>
          </div>
          {quotaExhausted ? (
            <Link to="/pricing" className="flex items-center gap-1 text-xs font-semibold text-syncchain-accent hover:text-syncchain-accent-hover transition-colors">
              <Zap className="w-3 h-3" /> Upgrade
            </Link>
          ) : (
            <span className="text-xs font-mono">{remaining("single_search")} remaining</span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-syncchain-accent to-syncchain-accent-hover mb-4 glow">
          <Search className="w-8 h-8 text-white" strokeWidth={2} />
        </div>
        <h1 className="font-heading text-3xl font-bold text-syncchain-text">
          Single Song Search
        </h1>
        <p className="text-syncchain-text-secondary mt-2">
          Search for track metadata using ISRC, Spotify, or title/artist
        </p>
      </div>

      {/* Search Card */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader className="pb-4">
          <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-syncchain-accent" />
            Search Parameters
          </CardTitle>
          <CardDescription className="text-syncchain-text-secondary">
            Choose your preferred search method
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-syncchain-surface mb-6">
              <TabsTrigger
                value="isrc"
                className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white"
                data-testid="tab-isrc"
              >
                <Disc className="w-4 h-4 mr-2" />
                ISRC
              </TabsTrigger>
              <TabsTrigger
                value="spotify"
                className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white"
                data-testid="tab-spotify"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Spotify
              </TabsTrigger>
              <TabsTrigger
                value="title"
                className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white"
                data-testid="tab-title"
              >
                <Music2 className="w-4 h-4 mr-2" />
                Title/Artist
              </TabsTrigger>
            </TabsList>

            <TabsContent value="isrc" className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                  ISRC Code
                </Label>
                <Input
                  value={isrc}
                  onChange={(e) => setIsrc(e.target.value.toUpperCase())}
                  placeholder="e.g., USRC12345678"
                  className="h-12 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent font-mono"
                  data-testid="isrc-input"
                />
                <p className="text-xs text-syncchain-text-muted">
                  International Standard Recording Code (12 characters)
                </p>
              </div>
            </TabsContent>

            <TabsContent value="spotify" className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                  Spotify URL or Track ID
                </Label>
                <Input
                  value={spotifyUrl}
                  onChange={(e) => setSpotifyUrl(e.target.value)}
                  placeholder="https://open.spotify.com/track/... or spotify:track:..."
                  className="h-12 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="spotify-input"
                />
                <p className="text-xs text-syncchain-text-muted">
                  Paste a Spotify track URL, URI, or track ID
                </p>
              </div>
            </TabsContent>

            <TabsContent value="title" className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                  Track Title *
                </Label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Bohemian Rhapsody"
                  className="h-12 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="title-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-wider text-syncchain-text-secondary">
                  Artist Name
                </Label>
                <Input
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder="e.g., Queen"
                  className="h-12 bg-syncchain-surface border-white/10 text-syncchain-text placeholder:text-syncchain-text-muted focus:border-syncchain-accent"
                  data-testid="artist-input"
                />
                <p className="text-xs text-syncchain-text-muted">
                  Optional, but improves accuracy
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <Button
            onClick={handleSearch}
            disabled={loading || quotaExhausted}
            className="w-full h-12 mt-6 bg-syncchain-accent hover:bg-syncchain-accent-hover text-white font-semibold rounded-full transition-all duration-300 glow-hover disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="search-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                Search Track
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card className="bg-syncchain-surface/50 border-white/5">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold text-syncchain-text mb-3">
            Search Tips
          </h3>
          <ul className="space-y-2 text-sm text-syncchain-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-syncchain-accent">•</span>
              <span>ISRC provides the most accurate match for recordings</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-syncchain-accent">•</span>
              <span>Spotify URLs include release date and label information</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-syncchain-accent">•</span>
              <span>Title + Artist search queries multiple databases</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-syncchain-accent">•</span>
              <span>Results are cached for 24 hours for faster retrieval</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewSearch;
